import api from './api';

export interface OKR {
  id?: string;
  nome: string;
  descricao: string;
  tipo: 'ESTRATEGICO' | 'CAPACIDADES_INTERNAS' | 'APROVEITAMENTO_OPORTUNIDADES' | 'MITIGACAO_RISCOS' | 'TATICO' | 'CULTURA_PESSOAS';
  status: 'ABERTO' | 'EM_PROGRESSO' | 'CONCLUIDO' | 'CANCELADO';
  responsavel?: string;
  periodoInicio?: string;
  periodoFim?: string;
  progresso?: number;
  keyResults?: KeyResult[];
  vinculacoes?: string[];
  empresaId?: string;
  dataCriacao?: string;
  dataAtualizacao?: string;
}

export interface KeyResult {
  id?: string;
  nome: string;
  descricao: string;
  tipo: 'QUANTITATIVO' | 'QUALITATIVO' | 'MILESTONE';
  valorInicial?: number;
  valorAlvo?: number;
  valorAtual?: number;
  unidadeMedida?: string;
  status: 'NAO_INICIADO' | 'EM_PROGRESSO' | 'CONCLUIDO' | 'ATRASADO';
  progresso?: number;
  checkIns?: CheckIn[];
  okrId?: string;
  dataCriacao?: string;
  dataAtualizacao?: string;
}

export interface CheckIn {
  id?: string;
  valor: number;
  comentario?: string;
  status: 'VERDE' | 'AMARELO' | 'VERMELHO';
  keyResultId?: string;
  usuario?: string;
  data?: string;
  dataCriacao?: string;
}

export interface OKRGeracaoDto {
  swotId?: string;
  metasGlobaisIds?: string[];
  planosAcaoIds?: string[];
  contextoAdicional?: string;
  quantidadeOKRs?: number;
}

export interface VinculacaoEstrategica {
  id: string;
  nome: string;
  tipo: 'SWOT' | 'META_GLOBAL' | 'PLANO_ACAO';
  descricao: string;
}

export interface ResultadoConsulta<T> {
  resultados: T[];
  total: number;
  pagina: number;
  tamanho: number;
  totalPaginas: number;
}

class OKRService {
  
  /**
   * Lista todos os OKRs
   */
  async listar(): Promise<OKR[]> {
    try {
      const response = await api.get('/api/okrs');
      return response.data;
    } catch (error) {
      console.error('Erro ao listar OKRs:', error);
      throw error;
    }
  }

  /**
   * Busca OKRs com filtros
   */
  async buscar(filtros: {
    tipo?: string;
    status?: string;
    responsavel?: string;
    periodoInicio?: string;
    periodoFim?: string;
  }): Promise<ResultadoConsulta<OKR>> {
    try {
      const params = new URLSearchParams();
      Object.entries(filtros).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/api/okrs/buscar?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar OKRs:', error);
      throw error;
    }
  }

  /**
   * Obtém um OKR por ID
   */
  async obterPorId(id: string): Promise<OKR> {
    try {
      const response = await api.get(`/api/okrs/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao obter OKR:', error);
      throw error;
    }
  }

  /**
   * Cria um novo OKR
   */
  async criar(okr: Omit<OKR, 'id'>): Promise<OKR> {
    try {
      const response = await api.post('/api/okrs', okr);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar OKR:', error);
      throw error;
    }
  }

  /**
   * Atualiza um OKR existente
   */
  async atualizar(id: string, okr: Partial<OKR>): Promise<OKR> {
    try {
      const response = await api.put(`/api/okrs/${id}`, okr);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar OKR:', error);
      throw error;
    }
  }

  /**
   * Exclui um OKR
   */
  async excluir(id: string): Promise<void> {
    try {
      await api.delete(`/api/okrs/${id}`);
    } catch (error) {
      console.error('Erro ao excluir OKR:', error);
      throw error;
    }
  }

  /**
   * Adiciona um Key Result a um OKR
   */
  async adicionarKeyResult(okrId: string, keyResult: Omit<KeyResult, 'id' | 'okrId'>): Promise<OKR> {
    try {
      const response = await api.post(`/api/okrs/${okrId}/key-results`, keyResult);
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar Key Result:', error);
      throw error;
    }
  }

  /**
   * Atualiza um Key Result
   */
  async atualizarKeyResult(okrId: string, keyResultId: string, keyResult: Partial<KeyResult>): Promise<OKR> {
    try {
      const response = await api.put(`/api/okrs/${okrId}/key-results/${keyResultId}`, keyResult);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar Key Result:', error);
      throw error;
    }
  }

  /**
   * Remove um Key Result
   */
  async removerKeyResult(okrId: string, keyResultId: string): Promise<OKR> {
    try {
      const response = await api.delete(`/api/okrs/${okrId}/key-results/${keyResultId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao remover Key Result:', error);
      throw error;
    }
  }

  /**
   * Adiciona um Check-in a um Key Result
   */
  async adicionarCheckIn(okrId: string, keyResultId: string, checkIn: Omit<CheckIn, 'id' | 'keyResultId'>): Promise<OKR> {
    try {
      const response = await api.post(`/api/okrs/${okrId}/key-results/${keyResultId}/check-ins`, checkIn);
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar Check-in:', error);
      throw error;
    }
  }

  /**
   * Obtém vinculações estratégicas disponíveis
   */
  async obterVinculacoesEstrategicas(): Promise<VinculacaoEstrategica[]> {
    try {
      const response = await api.get('/api/okrs/vinculacoes-estrategicas');
      return response.data;
    } catch (error) {
      console.error('Erro ao obter vinculações estratégicas:', error);
      throw error;
    }
  }

  /**
   * Gera OKRs usando IA
   */
  async gerarOKRs(dados: OKRGeracaoDto): Promise<OKR[]> {
    try {
      const response = await api.post('/api/okrs/gerar', dados);
      return response.data;
    } catch (error) {
      console.error('Erro ao gerar OKRs:', error);
      throw error;
    }
  }
}

export default new OKRService();
